import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLang } from '../hooks';
import './MusicPage.css';
import {
  getAlbums,
  getTracksByAlbum,
  getTracksSingles,
  getCoverPublicUrl,
  getTrackPublicUrl,
  type Album,
  type Track,
} from '../services/api/albums';

function formatDuration(sec: number | null) {
  if (sec == null) return '--:--';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function TrackRow({
  track,
  isPlaying,
  onPlay,
  locale,
}: {
  track: Track;
  isPlaying: boolean;
  onPlay: () => void;
  locale: string;
}) {
  const title = locale === 'th' && track.title_th ? track.title_th : track.title_en;
  const path = track.file_storage_path;
  const url = path ? getTrackPublicUrl(path) : null;

  return (
    <div className="track-row">
      <button type="button" onClick={onPlay} disabled={!url} className="track-row__play">
        <span className="track-row__icon">{isPlaying ? '▌▌' : '▶'}</span>
        <span className="track-row__title">{title}</span>
      </button>
      <div className="track-row__meta">
        <span className="track-row__duration">{formatDuration(track.duration_seconds)}</span>
        {url && (
          <a href={url} download className="track-row__download">Download</a>
        )}
      </div>
    </div>
  );
}

export default function MusicPage() {
  const { t } = useTranslation();
  const locale = useLang().lang;
  const [albums, setAlbums] = useState<Album[]>([]);
  const [singles, setSingles] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    Promise.all([getAlbums(), getTracksSingles()])
      .then(([a, s]) => {
        setAlbums(a);
        setSingles(s);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function play(url: string) {
    if (playingUrl === url) {
      audioRef.current?.pause();
      setPlayingUrl(null);
      return;
    }
    setPlayingUrl(url);
  }

  useEffect(() => {
    if (!audioRef.current) return;
    if (playingUrl) {
      audioRef.current.src = playingUrl;
      audioRef.current.play().catch(console.error);
    } else {
      audioRef.current.src = '';
    }
  }, [playingUrl]);

  if (loading) return <div className="page-loading">{t('common.loading')}</div>;

  return (
    <div className="music-page page">
      <audio ref={audioRef} controls className="music-page__player" />
      <h1 className="music-page__title">{t('nav.music')}</h1>

      {singles.length > 0 && (
        <section className="music-page__section">
          <h2 className="music-page__section-title">Singles</h2>
          <div className="music-page__track-list">
            {singles.map((track) => (
              <TrackRow
                key={track.id}
                track={track}
                isPlaying={playingUrl === (track.file_storage_path ? getTrackPublicUrl(track.file_storage_path) : null)}
                onPlay={() => track.file_storage_path && play(getTrackPublicUrl(track.file_storage_path))}
                locale={locale}
              />
            ))}
          </div>
        </section>
      )}

      {albums.map((album) => (
        <AlbumBlock key={album.id} album={album} locale={locale} playingUrl={playingUrl} onPlay={play} />
      ))}
      {albums.length === 0 && singles.length === 0 && <p className="page-empty">No music yet.</p>}
    </div>
  );
}

function AlbumBlock({
  album,
  locale,
  playingUrl,
  onPlay,
}: {
  album: Album;
  locale: string;
  playingUrl: string | null;
  onPlay: (url: string) => void;
}) {
  const [tracks, setTracks] = useState<Track[]>([]);
  const title = locale === 'th' && album.title_th ? album.title_th : album.title_en;

  useEffect(() => {
    getTracksByAlbum(album.id).then(setTracks).catch(console.error);
  }, [album.id]);

  return (
    <section className="album-block">
      <div className="album-block__header">
        {album.cover_storage_path && (
          <img src={getCoverPublicUrl(album.cover_storage_path)} alt="" className="album-block__cover" />
        )}
        <h2 className="album-block__title">{title}</h2>
      </div>
      <div className="music-page__track-list">
        {tracks.map((track) => (
          <TrackRow
            key={track.id}
            track={track}
            isPlaying={playingUrl === (track.file_storage_path ? getTrackPublicUrl(track.file_storage_path) : null)}
            onPlay={() => track.file_storage_path && onPlay(getTrackPublicUrl(track.file_storage_path))}
            locale={locale}
          />
        ))}
      </div>
    </section>
  );
}
